import { motion } from 'framer-motion';
import { ArrowLeft, Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';

const cookieRows = [
  {
    name: 'jq-cookie-consent',
    type: 'Técnica',
    provider: 'jonquintano.com',
    purpose: 'Recordar la elección del usuario sobre el uso de cookies (aceptar, esenciales o rechazar).',
    duration: 'Persistente (hasta que el usuario la borre)',
    essential: true,
  },
  {
    name: 'client_token',
    type: 'Técnica · Sesión',
    provider: 'jonquintano.com',
    purpose: 'Mantener iniciada la sesión del cliente en el Área Privada.',
    duration: '7 días',
    essential: true,
  },
  {
    name: 'admin_token',
    type: 'Técnica · Sesión',
    provider: 'jonquintano.com',
    purpose: 'Mantener iniciada la sesión del administrador en el panel.',
    duration: '7 días',
    essential: true,
  },
  {
    name: 'jq-theme',
    type: 'Preferencias',
    provider: 'jonquintano.com',
    purpose: 'Recordar la preferencia de tema (claro/oscuro) del visitante.',
    duration: 'Persistente',
    essential: true,
  },
  {
    name: 'jq-analytics-session',
    type: 'Analítica',
    provider: 'jonquintano.com',
    purpose: 'Identificador anónimo para contar visitantes únicos y medir el comportamiento agregado dentro del sitio.',
    duration: 'Sesión del navegador',
    essential: false,
  },
];

export const CookiePolicy = () => {
  return (
    <main className="min-h-screen bg-background pt-24 pb-16">
      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
          data-testid="cookie-policy-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
              <Cookie className="w-6 h-6" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl">Política de Cookies</h1>
          </div>

          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-muted-foreground">
            <p className="text-sm">
              Última actualización: {new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">1. ¿Qué son las cookies?</h2>
              <p>
                Las cookies son pequeños archivos de texto que los sitios web guardan en tu dispositivo
                (ordenador, móvil o tableta) cuando los visitas. Permiten recordar tus preferencias,
                mantener tu sesión iniciada o entender cómo se utiliza la web para mejorarla.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">2. Tipos de cookies que utilizamos</h2>
              <p>
                En <strong>Jon Quintano Fotografía</strong> usamos dos categorías de cookies:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong className="text-foreground">Cookies imprescindibles (técnicas y de preferencias).</strong>{' '}
                  Necesarias para que la web funcione: te permiten navegar, iniciar sesión en el área
                  de clientes, recordar tu elección sobre cookies o tu tema visual preferido.
                  No requieren consentimiento.
                </li>
                <li>
                  <strong className="text-foreground">Cookies analíticas (opcionales).</strong>{' '}
                  Recopilan información anónima y agregada sobre cómo los visitantes usan la web
                  (páginas más vistas, tiempo de permanencia, dispositivo). Nos ayudan a mejorar el
                  contenido y la experiencia. Requieren tu consentimiento explícito.
                </li>
              </ul>
              <p>
                <strong>No utilizamos cookies publicitarias ni de terceros para perfilado comercial.</strong>
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">3. Listado detallado</h2>
              <div className="overflow-x-auto -mx-4 md:mx-0">
                <table className="w-full text-sm border border-border" data-testid="cookies-table">
                  <thead className="bg-muted text-foreground">
                    <tr>
                      <th className="text-left px-3 py-2 border-b border-border">Nombre</th>
                      <th className="text-left px-3 py-2 border-b border-border">Tipo</th>
                      <th className="text-left px-3 py-2 border-b border-border">Origen</th>
                      <th className="text-left px-3 py-2 border-b border-border">Finalidad</th>
                      <th className="text-left px-3 py-2 border-b border-border">Duración</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cookieRows.map((c) => (
                      <tr key={c.name} className="align-top">
                        <td className="px-3 py-2 border-b border-border font-mono text-xs text-foreground">{c.name}</td>
                        <td className="px-3 py-2 border-b border-border">
                          {c.type}
                          {c.essential
                            ? <span className="ml-1 inline-block text-[10px] bg-green-500/15 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded">Imprescindible</span>
                            : <span className="ml-1 inline-block text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded">Opcional</span>}
                        </td>
                        <td className="px-3 py-2 border-b border-border">{c.provider}</td>
                        <td className="px-3 py-2 border-b border-border">{c.purpose}</td>
                        <td className="px-3 py-2 border-b border-border">{c.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">4. Cómo gestionar tu consentimiento</h2>
              <p>
                La primera vez que visitas la web aparece un banner en el que puedes elegir entre:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-foreground">Aceptar todas:</strong> permites cookies técnicas y analíticas.</li>
                <li><strong className="text-foreground">Solo imprescindibles:</strong> únicamente cookies técnicas y de preferencias.</li>
                <li><strong className="text-foreground">Rechazar:</strong> solo se mantienen las cookies estrictamente necesarias para el funcionamiento.</li>
              </ul>
              <p>
                Puedes cambiar tu elección en cualquier momento borrando los datos del sitio en tu navegador
                — el banner volverá a aparecer en la siguiente visita.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">5. Cómo desactivar las cookies en tu navegador</h2>
              <p>
                También puedes configurar tu navegador para que bloquee o elimine las cookies. Aquí tienes
                instrucciones oficiales:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><a className="hover:text-foreground underline" href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                <li><a className="hover:text-foreground underline" href="https://support.mozilla.org/es/kb/Borrar%20cookies" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                <li><a className="hover:text-foreground underline" href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
                <li><a className="hover:text-foreground underline" href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
              </ul>
              <p>
                Ten en cuenta que si bloqueas las cookies estrictamente necesarias, algunas funciones
                (como el inicio de sesión en el Área de Clientes) podrían dejar de funcionar.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">6. Más información</h2>
              <p>
                Para conocer cómo tratamos tus datos personales, consulta nuestra{' '}
                <Link to="/privacidad" className="underline hover:text-foreground">Política de Privacidad</Link>.
                Si tienes cualquier duda, escríbenos a{' '}
                <a href="mailto:info@jonquintano.com" className="underline hover:text-foreground">info@jonquintano.com</a>.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default CookiePolicy;
